import { useCallback, useEffect, useRef } from "react";
import _isEmpty from "lodash/isEmpty";
import { useSelector } from "react-redux";
import { useIntercom } from "react-use-intercom";
import { fetchIntercomJwt } from "app/api/intercom";
import config from "config";

/**
 * Intercom Integration Component
 * 
 * Handles Intercom Messenger initialization and user authentication with JWT.
 * 
 * IMPORTANT: Session State Management
 * 
 * When transitioning from anonymous to authenticated state, Intercom's internal
 * session state can become corrupted, leading to:
 * - 403 Forbidden errors with "App suspended" message
 * - Existing conversations cannot be opened
 * - Creating new messages fails
 * - Network errors on POST /messenger/web/conversations/:id
 * 
 * To prevent this without requiring a page reload, we use a proven pattern:
 * 1. Shutdown the current Intercom session
 * 2. Wait 500ms for cleanup
 * 3. Boot fresh with appId (resets Intercom state completely)
 * 4. Wait 100ms for initialization
 * 5. Update with user data and JWT authentication
 * 
 * This pattern fully resets the Intercom Messenger session and state without
 * requiring a full page reload, providing a seamless user experience.
 * 
 * JWT Authentication:
 * - JWT tokens are fetched from the backend and cached
 * - Tokens are automatically refreshed every 7 days
 * - JWT is included in the update() call via intercomUserJwt parameter
 * - Falls back to non-JWT boot if JWT fetch fails
 * 
 * @see https://community.intercom.com/api-webhooks-23/atintercom-messenger-js-sdk-with-react-7185
 * @see https://community.intercom.com/api-webhooks-23/intercom-fails-after-login-without-reload-13177
 * @see https://community.intercom.com/messenger-8/something-s-gone-wrong-next-js-10067?postid=29914#post29914
 */
export const Intercom = () => {
  const userObj = useSelector((store) => store.auth.user);
  const { boot, shutdown, update } = useIntercom();

  const jwtStateRef = useRef({ token: null, expiresAt: 0 });
  const previousUserIdRef = useRef(null);

  const requestIntercomJwt = useCallback(async () => {
    const responseBody = await fetchIntercomJwt();

    if (!responseBody || responseBody.status !== "OK" || !responseBody.data?.jwt) {
      throw new Error("Failed to retrieve Intercom JWT");
    }

    return {
      token: responseBody.data.jwt,
      expiresAt: responseBody.data.expiresAt ?? 0,
    };
  }, []);

  const refreshJwtAndBoot = useCallback(async () => {
    if (_isEmpty(userObj) || !userObj?.sub) {
      return;
    }

    const currentUserId = userObj.sub;

    // Only refresh if this is still the current user
    if (previousUserIdRef.current !== currentUserId) {
      return;
    }

    try {
      const freshJwt = await requestIntercomJwt();

      // Double-check user hasn't changed during async operation
      if (previousUserIdRef.current !== currentUserId) {
        return;
      }

      jwtStateRef.current = freshJwt;

      // Use the same pattern as bootForUser: shutdown -> wait -> boot with JWT -> wait -> update
      shutdown();
      await new Promise(resolve => setTimeout(resolve, 500));

      if (previousUserIdRef.current !== currentUserId) {
        return;
      }

      boot({ appId: config.intercom.APP_ID });

      await new Promise(resolve => setTimeout(resolve, 100));

      if (previousUserIdRef.current !== currentUserId) {
        return;
      }

      update({
        intercomUserJwt: freshJwt.token,
      });
    } catch (error) {
      console.error("Failed to refresh Intercom JWT:", error);
    }
  }, [userObj, boot, shutdown, update, requestIntercomJwt]);

  useEffect(() => {
    let isCancelled = false;

    const bootAnonymous = () => {
      shutdown();
      boot();
      // Don't call update() immediately after boot() - it interferes with initialization
    };

    const bootForUser = async () => {
      if (_isEmpty(userObj) || !userObj?.sub) {
        // User is anonymous or logged out
        bootAnonymous();
        return;
      }

      const currentUserId = userObj.sub;

      // Clear cached JWT if user changed to prevent token reuse across users
      const previousUserId = previousUserIdRef.current;
      if (previousUserId !== null && previousUserId !== currentUserId) {
        jwtStateRef.current = { token: null, expiresAt: 0 };
      }

      previousUserIdRef.current = currentUserId;

      // Capture the user ID at the start to detect user switches during async operations
      const userIdAtRequestStart = currentUserId;

      const nowInSeconds = Math.floor(Date.now() / 1000);
      let { token, expiresAt } = jwtStateRef.current;

      if (!token || !expiresAt || expiresAt <= nowInSeconds) {

        try {
          const freshJwt = await requestIntercomJwt();

          // Bail out if component unmounted, user changed, or user signed out
          if (isCancelled) {
            return;
          }

          // Check if the user changed during the async operation
          if (previousUserIdRef.current !== userIdAtRequestStart) {
            // User changed while fetching JWT - don't use stale token
            return;
          }

          token = freshJwt.token;
          expiresAt = freshJwt.expiresAt;
          jwtStateRef.current = freshJwt;
        } catch (error) {
          console.error("Failed to fetch Intercom JWT:", error);

          // Bail out if component unmounted or user changed
          if (isCancelled || previousUserIdRef.current !== userIdAtRequestStart) {
            return;
          }

          // Fallback to non-JWT boot if JWT fetch fails
          shutdown();
          await new Promise(resolve => setTimeout(resolve, 500));

          if (isCancelled || _isEmpty(userObj) || !userObj?.sub || userObj.sub !== userIdAtRequestStart) {
            return;
          }

          boot({ appId: config.intercom.APP_ID });
          return;
        }
      }

      // Boot Intercom with JWT authentication using the proven pattern:
      // shutdown -> wait -> boot fresh -> wait -> update with JWT
      // This pattern prevents "App suspended" errors when transitioning from anonymous to authenticated
      // without requiring a page reload
      shutdown();
      await new Promise(resolve => setTimeout(resolve, 500));

      // Bail out if component unmounted, user changed, or user signed out
      if (isCancelled) {
        return;
      }

      // Check if the user changed during the delay
      if (_isEmpty(userObj) || !userObj?.sub || userObj.sub !== userIdAtRequestStart) {
        // User changed or logged out during delay - don't proceed with stale data
        return;
      }

      // Step 2: Boot fresh with just app_id (resets Intercom state completely)
      // This is the key difference - boot fresh first, then update with user data
      boot({ appId: config.intercom.APP_ID });

      // Wait for Intercom to initialize, then update with user data
      await new Promise(resolve => setTimeout(resolve, 100));

      // Bail out again if user changed during the second delay
      if (isCancelled) {
        return;
      }

      if (_isEmpty(userObj) || !userObj?.sub || userObj.sub !== userIdAtRequestStart) {
        return;
      }

      update({
        intercomUserJwt: token,
      });
    };

    bootForUser();

    return () => {
      isCancelled = true;
    };
  }, [userObj, boot, shutdown, update, requestIntercomJwt]); // Include all dependencies to avoid stale closures

  // JWT refresh interval - refresh JWT and boot Intercom every 7 days to maintain session cookie
  useEffect(() => {
    if (_isEmpty(userObj) || !userObj?.sub) {
      return;
    }

    const SEVEN_DAYS_IN_MS = 7 * 24 * 60 * 60 * 1000;

    const refreshInterval = setInterval(() => {
      refreshJwtAndBoot();
    }, SEVEN_DAYS_IN_MS);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [userObj, refreshJwtAndBoot]);

  return null;
};